import { useMemo } from "react";
import { Users, Globe2 } from "lucide-react";
import { useRecitationActivity, flagUrl } from "@/lib/recitationActivity";

// Compact format helper. 1 234 → "1.2k", 12 345 → "12k", etc.
const formatN = (n) => {
  if (n == null) return "—";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return (n / 1000).toFixed(n < 10_000 ? 1 : 0).replace(/\.0$/, "") + "k";
  return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
};

// Single flag chip with image + country name (hover shows count).
const CountryChip = ({ country }) => {
  const img = flagUrl(country.code, 40);
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-card/80 border border-border text-[12px] text-foreground/80 shadow-sm"
      title={`${country.name} — ${formatN(country.count)}`}
      data-testid={`recitation-country-${country.code.toLowerCase()}`}
    >
      {img ? (
        <img
          src={img}
          alt={country.name}
          width={18}
          height={14}
          loading="lazy"
          decoding="async"
          className="rounded-sm shadow-[0_0_0_1px_rgba(0,0,0,0.06)]"
        />
      ) : (
        <span className="inline-block w-[18px] h-[14px] rounded-sm bg-muted text-[9px] flex items-center justify-center font-medium">
          {country.code}
        </span>
      )}
      <span className="font-medium">{country.name}</span>
      <span className="text-foreground/50 tabular-nums">{formatN(country.count)}</span>
    </div>
  );
};

// Landing-page hero banner. Big, breath-room layout designed to live in its
// own band on `/`. Always renders even on slow / empty data — graceful
// skeleton + zero-state copy keep the layout calm.
export const RecitationActivityBanner = () => {
  const data = useRecitationActivity(30_000);

  const chips = useMemo(() => {
    if (!data?.top_countries?.length) return [];
    return data.top_countries.slice(0, 8);
  }, [data]);

  const active = data?.active ?? null;
  const total  = data?.total_visitors ?? null;
  const countries = data?.country_count ?? null;

  return (
    <section
      className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10"
      aria-labelledby="recitation-activity-h"
      data-testid="recitation-activity-banner"
    >
      <div className="rounded-3xl border border-accent/35 bg-card/70 backdrop-blur-sm overflow-hidden shadow-sm">
        {/* TOP ROW — live + lifetime numbers */}
        <div className="grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          {/* Live now */}
          <div className="px-5 sm:px-7 py-5 sm:py-7">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-accent inline-flex items-center gap-2">
              <span className="relative inline-flex w-2 h-2" aria-hidden>
                <span className="absolute inset-0 rounded-full bg-emerald-500/70 animate-ping-slow" />
                <span className="relative inline-block w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              Live right now
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-4xl sm:text-5xl text-primary tabular-nums" data-testid="banner-active-count">
                {active == null ? "—" : formatN(active)}
              </span>
              <span className="text-foreground/65 text-sm">reciting the Quran</span>
            </div>
            <p className="mt-2 text-xs text-foreground/55">
              Updated every 30 seconds · counts users in Recite mode.
            </p>
          </div>

          {/* Total recited */}
          <div className="px-5 sm:px-7 py-5 sm:py-7">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-accent inline-flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Have recited so far
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-4xl sm:text-5xl text-foreground tabular-nums" data-testid="banner-total-count">
                {total == null ? "—" : formatN(total)}
              </span>
              <span className="text-foreground/65 text-sm">Muslims · alḥamdulillāh</span>
            </div>
            <p className="mt-2 text-xs text-foreground/55">
              Each soul recited at least one ayah on EasyQuran.UK.
            </p>
          </div>

          {/* Countries */}
          <div className="px-5 sm:px-7 py-5 sm:py-7">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-accent inline-flex items-center gap-2">
              <Globe2 className="w-3.5 h-3.5" /> Reaching
            </p>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-4xl sm:text-5xl text-foreground tabular-nums" data-testid="banner-country-count">
                {countries == null ? "—" : formatN(countries)}
              </span>
              <span className="text-foreground/65 text-sm">countries · worldwide</span>
            </div>
            <p className="mt-2 text-xs text-foreground/55">
              From Lahore to London, Cairo to Toronto — one ummah.
            </p>
          </div>
        </div>

        {/* BOTTOM STRIP — country chips */}
        <div className="px-5 sm:px-7 py-4 bg-muted/30 border-t border-border">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-foreground/55 mb-2">
            Top recitation regions
          </p>
          {chips.length === 0 ? (
            <p className="text-sm text-foreground/55">
              No reciters online yet — be the first by tapping <em>Start Reading</em> above.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="banner-country-chips">
              {chips.map(c => <CountryChip key={c.code} country={c} />)}
              {countries && countries > chips.length && (
                <span className="inline-flex items-center px-2 py-1 rounded-full bg-card/50 border border-dashed border-border text-[12px] text-foreground/55">
                  +{countries - chips.length} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
